import { isAxiosError } from 'axios';
import type { ApiErrorBody } from '@/lib/types/api.types';

/** Pure: the structured error body from an API failure, if present. */
export function getApiErrorBody(error: unknown): ApiErrorBody | undefined {
  if (!isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const { detail, code } = data as Partial<ApiErrorBody>;
  if (typeof detail !== 'string' || typeof code !== 'string') return undefined;
  return data as ApiErrorBody;
}

/** Pure: the machine error code from an API failure, if present. */
export function getApiErrorCode(error: unknown): string | undefined {
  return getApiErrorBody(error)?.code;
}

/**
 * Pure: true when a failure is an HTTP 404. Lets a caller tell "the resource
 * genuinely doesn't exist / isn't available yet" (an expected state worth a
 * neutral message) apart from a real failure (500, network/offline, etc.),
 * which still deserves the alarming/red treatment.
 */
export function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/** Pure: the human-readable detail from an API failure, if present. */
export function getApiErrorDetail(error: unknown): string | undefined {
  return getApiErrorBody(error)?.detail;
}

/**
 * Pure: the best human-readable message available for a failure, never undefined.
 *
 * `getApiErrorDetail` only speaks the app's own structured `{ code, detail }`
 * shape, and returns undefined for anything else. That is correct for reading a
 * business error code, but it made whole classes of failure invisible: a route
 * that isn't registered, or a query param the DTO rejects, comes back as Nest's
 * default `{ statusCode, message, error }` with no `code` and no `detail`, so
 * every caller fell through to its fallback - and callers with no fallback
 * rendered nothing at all. A delete failing with 404 showed the operator an
 * empty dialog while the console logged the error.
 *
 * Order matters: prefer the app's `detail` (written for users), then Nest's
 * `message` (a validation array or a router string - technical, but far better
 * than silence), then axios's own network message, then the fallback.
 *
 * @param error    Unknown thrown value from a mutation or query.
 * @param fallback Message to use when the error carries nothing usable.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const detail = getApiErrorDetail(error);
  if (detail) return detail;

  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (typeof data === 'object' && data !== null) {
      const { message } = data as { message?: unknown };
      // class-validator returns an array of per-field messages.
      if (Array.isArray(message) && message.length) return message.join('. ');
      if (typeof message === 'string' && message) return message;
    }
    // No response at all - offline, DNS, CORS, or a dev server that isn't up.
    if (!error.response && error.message) return error.message;
  }

  return fallback;
}

/**
 * Pure: pick the message for a failed mutation - the mapped string for a
 * known code, else the caller's generic fallback.
 */
export function mapApiError(
  error: unknown,
  messagesByCode: Record<string, string>,
  fallback: string,
): string {
  const code = getApiErrorCode(error);
  return (code && messagesByCode[code]) || fallback;
}
